import React, { useState, useEffect, useRef } from 'react';
import { FaCog, FaSave, FaBuilding, FaEnvelope, FaGlobe, FaImage } from 'react-icons/fa';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';

const C = {
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  bg: '#f4f4f9', surface: '#ffffff', border: '#e8e8f1', borderSoft: '#f0f0f6',
  success: '#10b981', successSoft: '#d1fae5',
  radius: 14, radiusSm: 8,
};

const inp = {
  padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm,
  fontSize: 13, outline: 'none', color: C.ink, background: C.surface,
  boxSizing: 'border-box', width: '100%', fontFamily: 'inherit',
};

const lbl = { fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 5, display: 'block' };

const TABS = [
  { key: 'company',  label: 'Company Info',  Icon: FaBuilding },
  { key: 'landing',  label: 'Landing Page',  Icon: FaGlobe    },
  { key: 'branding', label: 'Logo & Branding', Icon: FaImage  },
  { key: 'social',   label: 'Social Media',  Icon: FaEnvelope },
];

const AdminSiteSettings = () => {
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  const logoRef = useRef();

  const [companyInfo, setCompanyInfo] = useState({
    company_name: '', company_email: '', company_phone: '',
    company_address: '', company_city: '', company_state: '',
    company_pincode: '', company_gst: '', company_pan: '',
  });
  const [landingContent, setLandingContent] = useState({
    hero_title: '', hero_subtitle: '', hero_button_text: '',
    about_title: '', about_text: '', features_title: '',
    contact_email: '', contact_phone: '', support_email: '',
  });
  const [socialLinks, setSocialLinks] = useState({
    facebook_url: '', twitter_url: '', instagram_url: '', linkedin_url: '', youtube_url: '',
  });
  const [logoFile,    setLogoFile]    = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => { fetchSiteSettings(); }, []);

  const fetchSiteSettings = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.settings.getAll();
      const s = res.data.data || {};
      setCompanyInfo({
        company_name:    s.company_name    || 'A1PAY',
        company_email:   s.company_email   || 'support@a1pay.com',
        company_phone:   s.company_phone   || '+91-1234567890',
        company_address: s.company_address || '',
        company_city:    s.company_city    || '',
        company_state:   s.company_state   || '',
        company_pincode: s.company_pincode || '',
        company_gst:     s.company_gst     || '',
        company_pan:     s.company_pan     || '',
      });
      setLandingContent({
        hero_title:       s.hero_title       || 'Start Your Distribution Business Today',
        hero_subtitle:    s.hero_subtitle    || "Join India's fastest growing payment distribution network",
        hero_button_text: s.hero_button_text || 'Get Started',
        about_title:      s.about_title      || 'About A1PAY',
        about_text:       s.about_text       || 'A1PAY is a leading payment distribution platform...',
        features_title:   s.features_title   || 'Why Choose A1PAY?',
        contact_email:    s.contact_email    || 'contact@a1pay.com',
        contact_phone:    s.contact_phone    || '+91-1234567890',
        support_email:    s.support_email    || 'support@a1pay.com',
      });
      setSocialLinks({
        facebook_url:  s.facebook_url  || '',
        twitter_url:   s.twitter_url   || '',
        instagram_url: s.instagram_url || '',
        linkedin_url:  s.linkedin_url  || '',
        youtube_url:   s.youtube_url   || '',
      });
      setLogoPreview(s.logo_url || '');
    } catch { toast.error('Failed to load settings. Using default values.'); }
    finally { setLoading(false); }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo file size must be less than 2MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please upload a valid image file'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await adminAPI.settings.update({ ...companyInfo, ...landingContent, ...socialLinks });
      toast.success('Site settings updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update settings');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 80 }}>
      <Loader2 size={28} color={C.primary} style={{ animation: 'spin .8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Site Settings</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Manage your website content and company information.</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.surface, borderRadius: C.radius, padding: '6px', border: `1px solid ${C.border}` }}>
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            flex: 1, padding: '9px 14px', border: 'none', borderRadius: 10,
            background: activeTab === key ? C.primarySoft : 'transparent',
            color: activeTab === key ? C.primary : C.muted,
            fontWeight: activeTab === key ? 700 : 600, fontSize: 13,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all .12s',
          }}>
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Company Info */}
        {activeTab === 'company' && (
          <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaBuilding color={C.primary} /> Company Information
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { label: 'Company Name *', name: 'company_name',  type: 'text',  required: true  },
                { label: 'Company Email *', name: 'company_email', type: 'email', required: true  },
                { label: 'Company Phone *', name: 'company_phone', type: 'tel',   required: true  },
                { label: 'GST Number',      name: 'company_gst',   type: 'text',  ph: '22AAAAA0000A1Z5' },
                { label: 'PAN Number',      name: 'company_pan',   type: 'text',  ph: 'ABCDE1234F' },
              ].map(({ label, name, type, required, ph }) => (
                <div key={name}>
                  <label style={lbl}>{label}</label>
                  <input type={type} name={name} value={companyInfo[name]}
                    onChange={e => setCompanyInfo(p => ({ ...p, [name]: e.target.value }))}
                    placeholder={ph || ''} required={required} style={inp} />
                </div>
              ))}
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Company Address</label>
                <textarea name="company_address" value={companyInfo.company_address} rows={2}
                  onChange={e => setCompanyInfo(p => ({ ...p, company_address: e.target.value }))}
                  placeholder="Enter full address" style={{ ...inp, resize: 'vertical' }} />
              </div>
              {[
                { label: 'City',    name: 'company_city'    },
                { label: 'State',   name: 'company_state'   },
                { label: 'Pincode', name: 'company_pincode', ph: '123456' },
              ].map(({ label, name, ph }) => (
                <div key={name}>
                  <label style={lbl}>{label}</label>
                  <input type="text" name={name} value={companyInfo[name]} placeholder={ph || ''}
                    onChange={e => setCompanyInfo(p => ({ ...p, [name]: e.target.value }))} style={inp} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Landing Page */}
        {activeTab === 'landing' && (
          <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaGlobe color={C.primary} /> Landing Page Content
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Hero Section</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Hero Title *</label>
                <input type="text" value={landingContent.hero_title} required
                  onChange={e => setLandingContent(p => ({ ...p, hero_title: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Hero Subtitle *</label>
                <textarea value={landingContent.hero_subtitle} rows={2} required
                  onChange={e => setLandingContent(p => ({ ...p, hero_subtitle: e.target.value }))}
                  style={{ ...inp, resize: 'vertical' }} />
              </div>
              <div>
                <label style={lbl}>Hero Button Text</label>
                <input type="text" value={landingContent.hero_button_text}
                  onChange={e => setLandingContent(p => ({ ...p, hero_button_text: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>About Section</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>About Title</label>
                <input type="text" value={landingContent.about_title}
                  onChange={e => setLandingContent(p => ({ ...p, about_title: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>About Text</label>
                <textarea value={landingContent.about_text} rows={3}
                  onChange={e => setLandingContent(p => ({ ...p, about_text: e.target.value }))}
                  style={{ ...inp, resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Contact Information</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              {[
                { label: 'Contact Email', name: 'contact_email', type: 'email' },
                { label: 'Contact Phone', name: 'contact_phone', type: 'tel'   },
                { label: 'Support Email', name: 'support_email', type: 'email' },
              ].map(({ label, name, type }) => (
                <div key={name}>
                  <label style={lbl}>{label}</label>
                  <input type={type} value={landingContent[name]}
                    onChange={e => setLandingContent(p => ({ ...p, [name]: e.target.value }))} style={inp} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logo & Branding */}
        {activeTab === 'branding' && (
          <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaImage color={C.primary} /> Logo & Branding
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <label style={lbl}>Company Logo</label>
                <div
                  onClick={() => logoRef.current.click()}
                  style={{ border: `1.5px dashed ${C.border}`, borderRadius: C.radiusSm, padding: '20px 16px', cursor: 'pointer', textAlign: 'center', background: C.bg, fontSize: 13, color: C.muted }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="Logo" style={{ maxWidth: '100%', maxHeight: 80 }} />
                    : 'Click to upload logo (max 2MB, PNG/JPG)'}
                </div>
                <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Recommended: 200×60 px, PNG with transparent background</div>
              </div>
              <div style={{ background: C.bg, borderRadius: C.radiusSm, padding: 16, border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, marginBottom: 10 }}>Branding Guidelines</div>
                <ul style={{ fontSize: 12, color: C.body, paddingLeft: 18, margin: 0, lineHeight: 1.8 }}>
                  <li>Use transparent background (PNG format)</li>
                  <li>Visible on both light and dark backgrounds</li>
                  <li>Maintain aspect ratio when resizing</li>
                  <li>Use high-resolution images for best quality</li>
                </ul>
                <div style={{ marginTop: 16, fontSize: 12, color: C.muted, fontWeight: 600 }}>Brand Colors:</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  {[['#7c3aed', 'Primary'], ['#14142b', 'Dark'], ['#f1ebff', 'Soft']].map(([color, name]) => (
                    <div key={color} style={{ textAlign: 'center' }}>
                      <div style={{ width: 44, height: 44, borderRadius: 8, background: color, border: `1px solid ${C.border}` }} />
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Social Media */}
        {activeTab === 'social' && (
          <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaEnvelope color={C.primary} /> Social Media Links
            </div>
            <div style={{ background: C.primarySoft, border: `1px solid ${C.primaryTint}`, borderRadius: C.radiusSm, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: C.body }}>
              Add your social media profile URLs. These links will appear in the website footer. Leave blank to hide any icon.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { label: 'Facebook URL',  name: 'facebook_url',  ph: 'https://facebook.com/yourpage'         },
                { label: 'Twitter URL',   name: 'twitter_url',   ph: 'https://twitter.com/yourhandle'        },
                { label: 'Instagram URL', name: 'instagram_url', ph: 'https://instagram.com/yourprofile'     },
                { label: 'LinkedIn URL',  name: 'linkedin_url',  ph: 'https://linkedin.com/company/yourco'   },
                { label: 'YouTube URL',   name: 'youtube_url',   ph: 'https://youtube.com/yourchannel'       },
              ].map(({ label, name, ph }) => (
                <div key={name}>
                  <label style={lbl}>{label}</label>
                  <input type="url" value={socialLinks[name]} placeholder={ph}
                    onChange={e => setSocialLinks(p => ({ ...p, [name]: e.target.value }))} style={inp} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
          <button type="button" onClick={() => fetchSiteSettings()} style={{
            padding: '9px 20px', borderRadius: C.radiusSm, border: `1px solid ${C.border}`,
            background: C.surface, color: C.body, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Reset</button>
          <button type="submit" disabled={saving} style={{
            padding: '9px 24px', borderRadius: C.radiusSm, background: C.primary,
            color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8, opacity: saving ? .7 : 1,
          }}>
            {saving ? <Loader2 size={14} style={{ animation: 'spin .8s linear infinite' }} /> : <FaSave />}
            {saving ? 'Saving…' : 'Save All Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSiteSettings;
